/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { EnvironmentThemeProvider } from '@codex-brightworld/themes';
import { Rating, Button } from '@codex-brightworld/basics';
import { formatPrice, formatWasPriceDate } from '@codex/helpers';
import colors from '@codex/colors';
import { Icon } from '@codex-brightworld/icons';
import ProductPriceBlockWrapper, { VARIANT_FO_SITE_PSA }
  from '../../../ProductPriceBlock/ProductPriceBlockWrapper';
import { AddToBasketButton } from '../AddToBasketButton/AddToBasketButton';
import BuyTogetherRibbon from '../BuyTogetherRibbon/BuyTogetherRibbon';

const FlexContainer = styled.div`
  @font-face {
    font-family: 'CurrysSans';
    src: url('https://currys-ssl.cdn.dixons.com/css/themes/fonts/currys/CurrysSans-Regular.woff') format('woff'),
        url('https://currys-ssl.cdn.dixons.com/css/themes/fonts/currys/CurrysSans-Regular.otf') format('truetype'),
        url('https://currys-ssl.cdn.dixons.com/css/themes/fonts/currys/CurrysSans-Regular.eot?') format('embedded-opentype');
  }

  ${({ buyTogether }) => css`
    display: inline-flex;
    margin-top: ${buyTogether ? '14px' : '0'};
  
    @media ${(props) => props.theme.breakpoints.mobile} {
      width: 100%;
      margin-top: ${buyTogether ? '9px' : '0'};
    }
  `}
`;

const FlexContainerImg = styled.div`
  img {
    width: 135px;
    height: 120px
  }

  max-width: 135px;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    display: none;
  }
`;

const FlexBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 192px;
  margin-left: 22px;
  flex-wrap: wrap;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    margin-left: 0;
    width: 100%;
  }
`;

const FlexMobile = styled.div`
  display: inline-flex;
  width: 100%;
`;

const ImgWrapper = styled.div`
  img {
    width: 100px;
    height: 89px;
  }

  width: 100px;
  padding-bottom: 10px;
  
  @media (min-width: 600px) {
    display: none;
  }
`;

const TitleWrapper = styled.div`
  font-family: CurrysSans;
  font-size: 14px;
  color: ${(props) => props.theme.colors.neutral.currysBlack};
  margin-bottom: 10px;
  width: 192px;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    width: 100%;
  }
`;

const StyledRating = styled(Rating)`
  padding-bottom: 10px;
  
  > div > span {
    /* Fix for Safari browser */
    display: block;
  }
`;

const TitleRatingWrapper = styled.div`
  @media ${(props) => props.theme.breakpoints.mobile} {
    margin-left: 9px;
  }
`;

const ProductPriceBlockContainer = styled.div`
  padding-bottom: 10px;
  width: 192px;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    width: 100%;
  }
`;

const AddAttachmentInBasketWrapper = styled.div`
  width: 192px;
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

const StyledAddedToBasketButton = styled(Button)`
  background-color: ${colors.positive.hover}
`;

const AddedToBasketIcon = styled(Icon)`
  width: 20px;
  height: 20px;
  margin: -5px 10px -5px 0px;
`;

const AddedToBasketButton = ({ buttonLabel }) => (
  <StyledAddedToBasketButton positive>
    <AddedToBasketIcon icon={'tick'} />
    {buttonLabel}
  </StyledAddedToBasketButton>
);

AddedToBasketButton.propTypes = {
  buttonLabel: PropTypes.string
};

AddedToBasketButton.defaultProps = {
  buttonLabel: ''
};

export default function AttachmentBlock(props) {
  const {
    attachmentImgUrl,
    attachmentName,
    attachmentBrandLabel,
    ratingCount,
    ratingValue,
    currentPrice,
    wasPrice,
    currencyCode,
    addAttachmentInBasketFupid,
    addAttachmentInBasketQuantity,
    storeId,
    deliveryType,
    addAttachmentInBasketLabel,
    inProgressLabel,
    attachmentAddedInBasketLabel,
    onAddToBasket,
    onAddToBasketSuccess,
    onAddToBasketFailure,
    buyTogetherPrice,
    psaWhenBoughtWithEnabled,
    productID,
    storeUrl
  } = props;

  let attachmentTitle = attachmentName;
  if (attachmentName.indexOf(attachmentBrandLabel) === -1) {
    attachmentTitle = `${attachmentBrandLabel} ${attachmentName}`;
  }

  let buyTogether = false;
  let buyTogetherSavePrice = 0;
  if (buyTogetherPrice && currentPrice - buyTogetherPrice > 0) {
    buyTogether = true;
    buyTogetherSavePrice = currentPrice - buyTogetherPrice;
  }

  return (
    <EnvironmentThemeProvider>
      <div>
        {buyTogetherSavePrice > 0 &&
          <BuyTogetherRibbon price={buyTogetherSavePrice} currency={currencyCode} />}
        <FlexContainer buyTogether={buyTogether}>
          <FlexContainerImg>
            <img src={attachmentImgUrl} alt={attachmentTitle} />
          </FlexContainerImg>
          <FlexBox>
            <FlexMobile>
              <ImgWrapper>
                <img src={attachmentImgUrl} alt={attachmentTitle} />
              </ImgWrapper>
              <TitleRatingWrapper>
                <TitleWrapper>{attachmentTitle}</TitleWrapper>
                <StyledRating
                  maxCount={5}
                  ratingCount={ratingCount}
                  ratingValue={ratingValue}
                />
              </TitleRatingWrapper>
            </FlexMobile>
            <ProductPriceBlockContainer>
              {wasPrice &&
                wasPrice.amount &&
                wasPrice.discountAmount &&
                wasPrice.dateFrom &&
                wasPrice.dateTo &&
                !buyTogetherPrice
                ? (
                  <ProductPriceBlockWrapper
                    variant={VARIANT_FO_SITE_PSA}
                    currentPrice={formatPrice(currentPrice, true, currencyCode)}
                    wasPrice={formatPrice(wasPrice.amount, true, currencyCode)}
                    wasPriceFrom={formatWasPriceDate(wasPrice.dateFrom)}
                    wasPriceTo={formatWasPriceDate(wasPrice.dateTo)}
                    savingsPrice={formatPrice(wasPrice.discountAmount, true, currencyCode)}
                  />
                ) : (
                  <ProductPriceBlockWrapper
                    variant={VARIANT_FO_SITE_PSA}
                    currentPrice={formatPrice(currentPrice, true, currencyCode)}
                    buyTogetherPrice={buyTogetherSavePrice > 0 ? buyTogetherPrice : null}
                  />
                )}
            </ProductPriceBlockContainer>
            <AddAttachmentInBasketWrapper>
              <AddToBasketButton
                fupid={addAttachmentInBasketFupid}
                quantity={addAttachmentInBasketQuantity}
                storeId={storeId}
                deliveryType={deliveryType}
                label={addAttachmentInBasketLabel}
                inProgressLabel={inProgressLabel}
                addedLabel={attachmentAddedInBasketLabel}
                onAddToBasket={onAddToBasket}
                onAddToBasketSuccess={onAddToBasketSuccess}
                onAddToBasketFailure={onAddToBasketFailure}
                psaWhenBoughtWithEnabled={psaWhenBoughtWithEnabled}
                productID={productID}
                storeUrl={storeUrl}
              />
            </AddAttachmentInBasketWrapper>
          </FlexBox>
        </FlexContainer>
      </div>
    </EnvironmentThemeProvider>
  );
}

// TODO: add missing 'wasPrice' proptypes

AttachmentBlock.propTypes = {
  attachmentName: PropTypes.string.isRequired,
  attachmentBrandLabel: PropTypes.string.isRequired,
  attachmentImgUrl: PropTypes.string.isRequired,
  ratingCount: PropTypes.number.isRequired,
  ratingValue: PropTypes.number.isRequired,
  currentPrice: PropTypes.number.isRequired,
  wasPrice: PropTypes.shape({
    amount: PropTypes.number,
    discountAmount: PropTypes.number
  }),
  currencyCode: PropTypes.string,
  addAttachmentInBasketFupid: PropTypes.string.isRequired,
  addAttachmentInBasketQuantity: PropTypes.number.isRequired,
  storeId: PropTypes.number,
  deliveryType: PropTypes.oneOf(['RESERVE_AND_COLLECT', 'PAY_AND_COLLECT', 'PAY_AND_COLLECT_FROM_STORE_STOCK']),
  addAttachmentInBasketLabel: PropTypes.string.isRequired,
  attachmentAddedInBasketLabel: PropTypes.string.isRequired,
  inProgressLabel: PropTypes.string.isRequired,
  buyTogetherPrice: PropTypes.number,
  psaWhenBoughtWithEnabled: PropTypes.bool.isRequired,
  productID: PropTypes.string.isRequired,
  onAddToBasket: PropTypes.func,
  onAddToBasketSuccess: PropTypes.func,
  onAddToBasketFailure: PropTypes.func,
  storeUrl: PropTypes.string.isRequired
};

AttachmentBlock.defaultProps = {
  wasPrice: null,
  currencyCode: 'GBP',
  onAddToBasket: () => {},
  onAddToBasketSuccess: () => {},
  onAddToBasketFailure: () => {},
  storeId: undefined,
  deliveryType: undefined,
  buyTogetherPrice: 0
};
